import { observable, action, makeObservable } from "mobx";
import { ColorStyle } from "src/utils/Colors";
import { Notification } from "src/types";
import { RootStore } from "./RootStore";

const headerHeights = {
    mobile: 70,
    tablet: 80,
    desktop: 80
};

export enum ScreenWidth {
    SM = 576,
    MD = 768,
    LG = 992,
    XL = 1200
}

export enum RealestatesListType {
    GRID,
    LIST
}

export class UIStore {
    rootStore: RootStore;
    @observable showNavigationBar = true;
    @observable isMobile = false;
    @observable showSearchbar = false;
    @observable closeAppbar = false;
    @observable headerHeight = headerHeights.desktop;
    @observable subHeaderHeight = 51;
    @observable backgroundColor = ColorStyle("white");
    @observable shouldShowLoginNotification: boolean = false;
    @observable notifications: Notification[] = [];
    @observable displayedNotifications: number[] = [];
    @observable defaultColumnSize: { md: { span: number; offset: number } } = { md: { span: 6, offset: 3 } };
    @observable displayChangeLanguageDropdown = false;
    @observable currentLanguageDropdownValue: string = "";

    mobileWidth = 576;
    tabletWidth = 768;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeObservable(this);
    }

    getDisplaySize = () => {
        if (window.innerWidth <= this.mobileWidth) {
            return headerHeights.mobile;
        } else if (window.innerWidth > this.mobileWidth && window.innerWidth <= this.tabletWidth) {
            return headerHeights.tablet;
        } else {
            return headerHeights.desktop;
        }
    };

    @action
    setSearchbar = (toggle: boolean) => {
        this.setHeaderHeight(toggle ? headerHeights.desktop : undefined);
        this.showSearchbar = toggle;
    };

    @action
    setIsMobile = (isMobile: boolean) => {
        this.isMobile = isMobile;
    };

    @action
    setHeaderHeight(overwriteHeight?: number) {
        this.headerHeight =
            overwriteHeight !== undefined
                ? overwriteHeight
                : this.isMobile
                ? headerHeights.mobile
                : headerHeights.desktop;
    }

    @action
    setCloseAppBar = (closeAppBar: boolean) => {
        this.closeAppbar = closeAppBar;
    };

    @action
    setBackgroundColor = (backgroundColor: string) => {
        this.backgroundColor = backgroundColor;
    };

    @action
    setShouldShowLoginNotification = (shouldShow: boolean) => {
        this.shouldShowLoginNotification = shouldShow;
    };

    @action
    enqueueSnackbar = (note: Notification) => {
        this.notifications.push({
            key: new Date().getTime() + Math.random(),
            ...note
        });
    };

    @action
    removeSnackbar = (note: Notification) => {
        this.notifications = this.notifications.filter((not) => not.key !== note.key);
    };

    @action
    storeDisplayedNotification = (key: number) => {
        this.displayedNotifications = [...this.displayedNotifications, key];
    };

    @action
    private onWindowResize = () => {
        this.setIsMobile(window.innerWidth <= 768);
        this.setHeaderHeight(this.getDisplaySize());
    };

    @action
    reset = () => {
        this.shouldShowLoginNotification = false;
        this.notifications = [];
        this.displayedNotifications = [];
    };

    @action
    setDisplayChangeLanguageDropdown = (displayChangeLanguageDropdown: boolean) => {
        this.displayChangeLanguageDropdown = displayChangeLanguageDropdown;
    };

    @action
    setCurrentLanguageDropdownValue = (currentLanguageDropdownValue: string) => {
        this.currentLanguageDropdownValue = currentLanguageDropdownValue;
    };
}
